import React, { createContext, useState, useEffect, ReactNode, useContext, useMemo, useCallback } from 'react';
import CognoraLogo from '../assets/svg/CognoraLogo.svg';
import { useQueryClient, QueryClient } from 'react-query';

// Theme context type definition
interface ThemeContextType {
  theme: 'light' | 'dark' | 'system';
  resolvedTheme: 'light' | 'dark';
  toggleTheme: () => void;
  setSystemTheme: () => void;
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
}

interface WindowContextProps {
  windowWidth: number;
  logoImage: string;
  queryClient: QueryClient;
  theme: 'light' | 'dark' | 'system';
  resolvedTheme: 'light' | 'dark';
  toggleTheme: () => void;
  setSystemTheme: () => void;
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  fontSize: string;
  setFontSize: (fontSize: string) => void;
  confirmationModalState: {
    isOpen: boolean;
    text: string;
    text2: string;
    onConfirm: () => void;
  };
  openConfirmationModal: (text: string, onConfirm: () => void, text2: string | null) => void;
  closeConfirmationModal: () => void;
  isNewWindowModalOpen: boolean;
  newWindowModalLink: string;
  openNewWindowModal: (link: string) => void;
  addAllowedLink: (link: string) => void;
  closeNewWindowModal: () => void;
  codeDetectionState: {
    isOpen: boolean;
    codeContent: string;
    onPasteAnyway: () => void;
  };
  openCodeDetectionModal: (codeContent: string, onPasteAnyway: () => void) => void;
  closeCodeDetectionModal: () => void;
  }

export const WindowContext = createContext<WindowContextProps>({
  windowWidth: window.innerWidth,
  logoImage: CognoraLogo,
  queryClient: new QueryClient(),
  theme: 'light',
  resolvedTheme: 'light',
  toggleTheme: () => {},
  setSystemTheme: () => {},
  setTheme: () => {},
  fontSize: 'medium',
  setFontSize: () => {},
  confirmationModalState: {
    isOpen: false,
    text: '',
    text2: '',
    onConfirm: () => {},
  },
  openConfirmationModal: () => {},
  closeConfirmationModal: () => {},
  isNewWindowModalOpen: false,
  newWindowModalLink: '',
  openNewWindowModal: () => {},
  closeNewWindowModal: () => {},
  addAllowedLink: () => {},
  codeDetectionState: {
    isOpen: false,
    codeContent: '',
    onPasteAnyway: () => {},
  },
  openCodeDetectionModal: () => {},
  closeCodeDetectionModal: () => {},
});

interface WindowProviderProps {
  children: ReactNode;
}

export const WindowProvider: React.FC<WindowProviderProps> = ({ children }) => {
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
  const queryClient = useQueryClient();
  const [confirmationModalState, setConfirmationModalState] = useState<{
    isOpen: boolean;
    text: string;
    text2: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    text: '',
    text2: '',
    onConfirm: () => {},
  });
  const [isNewWindowModalOpen, setIsNewWindowModalOpen] = useState<boolean>(false);
  const [newWindowModalLink, setNewWindowModalLink] = useState<string>('');
  const [allowedLinks, setAllowedLinks] = useState<string[]>([]);
  const [codeDetectionState, setCodeDetectionState] = useState<{
    isOpen: boolean;
    codeContent: string;
    onPasteAnyway: () => void;
  }>({
    isOpen: false,
    codeContent: '',
    onPasteAnyway: () => {},
  });

  // Theme state management
  const [theme, setTheme] = useState<'light' | 'dark' | 'system'>(() => {
    const savedTheme = localStorage.getItem('theme');
    return (savedTheme as 'light' | 'dark' | 'system') || 'system';
  });

  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return theme;
  });

  // Logo state based on theme
  const [logoImage, setLogoImage] = useState(CognoraLogo);

  const [fontSize, setFontSize] = useState<string>(() => {
    return localStorage.getItem('fontSize') || 'medium';
  });

  // Theme effect
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      if (theme === 'system') {
        const newResolvedTheme = e.matches ? 'dark' : 'light';
        setResolvedTheme(newResolvedTheme);
        document.documentElement.setAttribute('data-theme', newResolvedTheme);
        setLogoImage(CognoraLogo);
      }
    };

    // Apply the theme initially
    if (theme === 'system') {
      const initialResolvedTheme = mediaQuery.matches ? 'dark' : 'light';
      setResolvedTheme(initialResolvedTheme);
      document.documentElement.setAttribute('data-theme', initialResolvedTheme);
      setLogoImage(CognoraLogo);
    } else {
      setResolvedTheme(theme);
      document.documentElement.setAttribute('data-theme', theme);
      setLogoImage(CognoraLogo);
    }

    // Add event listener for system theme changes
    mediaQuery.addEventListener('change', handleChange);

    // Cleanup listener on unmount or when theme changes
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [theme]);

  const openConfirmationModal = useCallback((text: string, onConfirm: () => void, text2: string | null = null) => {
    setConfirmationModalState({
      isOpen: true,
      text,
      text2: text2 || '',
      onConfirm,
    });
  }, []);

  const closeConfirmationModal = useCallback(() => {
    setConfirmationModalState({
      isOpen: false,
      text: '',
      text2: '',
      onConfirm: () => {},
    });
  }, []);

  const openNewWindowModal = useCallback((link: string) => {
    if (allowedLinks.includes(link)) {
      window.open(link, '_blank');
    } else {
      setIsNewWindowModalOpen(true);
      setNewWindowModalLink(link);
    }
  }, [allowedLinks]);

  const addAllowedLink = useCallback((link: string) => {
    setAllowedLinks([...allowedLinks, link]);
  }, [allowedLinks]);

  const closeNewWindowModal = useCallback(() => {
    setIsNewWindowModalOpen(false);
    setNewWindowModalLink('');
  }, []);
  
  const openCodeDetectionModal = useCallback((codeContent: string, onPasteAnyway: () => void) => {
    setCodeDetectionState({
      isOpen: true,
      codeContent,
      onPasteAnyway
    });
  }, []);

  const closeCodeDetectionModal = useCallback(() => {
    setCodeDetectionState({
      isOpen: false,
      codeContent: '',
      onPasteAnyway: () => {}
    });
  }, []);

  // Font size scaling
  useEffect(() => {
    const setFontSizeScale = () => {
      let scaleFactor;
      switch (fontSize) {
        case 'small': scaleFactor = '0.875'; break;  // 87.5%
        case 'medium': scaleFactor = '1'; break;     // 100%
        case 'large': scaleFactor = '1.125'; break;  // 112.5%
        default: scaleFactor = '1';
      }
      
      // Apply the scale factor as a CSS variable
      document.documentElement.style.setProperty('--font-size-scale', scaleFactor);
    };

    setFontSizeScale();
    window.addEventListener('storage', setFontSizeScale);
    return () => window.removeEventListener('storage', setFontSizeScale);
  }, [fontSize]);

  // Window resize effect
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Theme toggle functions
  const toggleTheme = useCallback(() => {
    let newTheme: 'light' | 'dark' | 'system';
    if (theme === 'light') {
      newTheme = 'dark';
    } else if (theme === 'dark') {
      newTheme = 'system';
    } else {
      newTheme = 'light';
    }
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);

    if (newTheme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      setResolvedTheme(systemTheme);
      document.documentElement.setAttribute('data-theme', systemTheme);
      setLogoImage(CognoraLogo);
    } else {
      setResolvedTheme(newTheme);
      document.documentElement.setAttribute('data-theme', newTheme);
      setLogoImage(CognoraLogo);
    }
  }, [theme]);

  const setSystemTheme = useCallback(() => {
    setTheme('system');
    localStorage.setItem('theme', 'system');
    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    setResolvedTheme(systemTheme);
    document.documentElement.setAttribute('data-theme', systemTheme);
    setLogoImage(CognoraLogo);
  }, []);

  // Create a wrapped setTheme function that also updates localStorage
  const setThemeWithStorage = useCallback((newTheme: 'light' | 'dark' | 'system') => {
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    
    if (newTheme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      setResolvedTheme(systemTheme);
      document.documentElement.setAttribute('data-theme', systemTheme);
      setLogoImage(CognoraLogo);
    } else {
      setResolvedTheme(newTheme);
      document.documentElement.setAttribute('data-theme', newTheme);
      setLogoImage(CognoraLogo);
    }
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    windowWidth,
    logoImage,
    queryClient,
    theme,
    resolvedTheme,
    toggleTheme,
    setSystemTheme,
    setTheme: setThemeWithStorage,
    fontSize,
    setFontSize,
    confirmationModalState,
    openConfirmationModal,
    closeConfirmationModal,
    isNewWindowModalOpen,
    newWindowModalLink,
    openNewWindowModal,
    addAllowedLink,
    closeNewWindowModal,
    codeDetectionState,
    openCodeDetectionModal,
    closeCodeDetectionModal,
  }), [windowWidth, logoImage, queryClient, theme, resolvedTheme, toggleTheme, setSystemTheme, setThemeWithStorage, fontSize, setFontSize, confirmationModalState, openConfirmationModal, closeConfirmationModal, isNewWindowModalOpen, newWindowModalLink, openNewWindowModal, addAllowedLink, closeNewWindowModal, codeDetectionState, openCodeDetectionModal, closeCodeDetectionModal]);

  return (
    <WindowContext.Provider value={contextValue}>
      {children}
    </WindowContext.Provider>
  );
};

export const useWindow = () => {
  return useContext(WindowContext);
};

// Added theme hook for backward compatibility
export const useTheme = (): ThemeContextType => {
  const { theme, resolvedTheme, toggleTheme, setSystemTheme, setTheme } = useContext(WindowContext);
  return { theme, resolvedTheme, toggleTheme, setSystemTheme, setTheme };
};
